import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';

import { cutoffs } from '../../constants';
import { updateSearchParams } from '../../helpers/urlHelpers';

import Tiles from '../Tiles/Tiles';
import LoadMore from '../Tiles/LoadMore';
import { RecommendedForYouLoader } from '../RecommendedForYou/RecommendedForYouLoader';
import { ServerVarsContextConsumer } from '../../../sharedComponents/ServerVarsContext';

import actions from '../../actions/FavoritesActions';
import get from 'lodash.get';
import Pagination from '../../../sharedComponents/Pagination/Pagination';

class ItemsRefetchWrapper extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isClient: false,
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        window.onpopstate = this.handlePopState;
        const totalResults = get(this.props, 'portfolioNode.items.totalResults');

        if (totalResults >= 0) {
            this.props.setTotalResults(totalResults);
        }
        this.setState({ isClient: true });
    }

    componentDidUpdate(prevProps) {
        const prevTotalResults = get(prevProps, 'portfolioNode.items.totalResults');
        const totalResults = get(this.props, 'portfolioNode.items.totalResults');
        const isPageChange = this.props.page !== prevProps.page;

        if (prevTotalResults !== totalResults) {
            this.props.setTotalResults(totalResults);
        }

        if (isPageChange) {
            this.props.setFullPageLoading(true);
            const refetchVariables = fragmentVariables => ({
                ...fragmentVariables,
                portfolioIds: [this.props.portfolioNode.serviceId],
                page: this.props.page,
            });

            this.props.relay.refetch(
                refetchVariables,
                null,
                () => {
                    this.props.setFullPageLoading(false);
                },
                { force: true }
            );
        }
    }

    componentWillUnmount() {
        this.props.setPartialPageLoading(false);
        window.onpopstate = null;
    }

    onPageChange = (event, nextPage) => {
        event.preventDefault();
        this.props.resetUiState({ retainItems: true, retainSort: true, retainSearch: true });
        this.props.setPage(nextPage);
        updateSearchParams('page', nextPage);
        window.scrollTo(0, 0);
    };

    handlePopState = () => {
        if (window.location.search) {
            let params = new URLSearchParams(window.location.search);

            if (params.get('page')) {
                this.props.setPage(+params.get('page'));
            }

            if (params.get('sort')) {
                this.props.setSortBy(params.get('sort'));
            }

            if (params.get('search')) {
                this.props.setSearchTerm(params.get('search'));
            }
        } else {
            this.props.setPage(1);
        }
    };

    render() {
        const { first, isLoadingPartialPage, isPublic, currency, user, relay } = this.props;
        const { isClient } = this.state;
        const portfolioNode = get(this.props, 'portfolioNode') || {};
        const portfolioItems = portfolioNode.items || {};
        const items = get(this.props, 'portfolioNode.items.edges', []);
        const totalResults = portfolioItems.totalResults;
        const numberOfPages = portfolioItems.numberOfPages;
        const hasItems = !!items.length;

        if (!totalResults) {
            return null;
        }

        return (
            <Fragment>
                <Tiles
                    addNote={this.props.addNote}
                    currency={this.props.currency}
                    onDrop={this.props.onDrop}
                    isPublic={isPublic}
                    portfolioItemsConnection={portfolioItems}
                    reordered={this.props.reordered}
                    showDealerBranding={this.props.showDealerBranding}
                    showProjects={this.props.showProjects}
                    type={this.props.type}
                    user={this.props.user}
                    viewer={this.props.viewer || null}
                    viewMode={this.props.viewMode}
                />
                <Pagination
                    page={this.props.page}
                    numberOfPages={numberOfPages}
                    onPageChange={this.onPageChange}
                    {...this.props}
                />
                <ServerVarsContextConsumer>
                    {({ isDesktop }) => (
                        <React.Fragment>
                            {isDesktop &&
                            isClient &&
                            hasItems &&
                            false && ( // temp disable FINDING-13831
                                    <RecommendedForYouLoader
                                        items={items}
                                        isClient={isClient}
                                        currency={currency}
                                        userId={get(user, 'serviceId')}
                                        environment={get(relay, 'environment')}
                                        isVerifiedTrade={get(user, 'isVerifiedTrade')}
                                    />
                                )}
                        </React.Fragment>
                    )}
                </ServerVarsContextConsumer>
            </Fragment>
        );
    }
}

ItemsRefetchWrapper.propTypes = {
    first: PropTypes.number,
    isLoadingPartialPage: PropTypes.bool,
    isPublic: PropTypes.bool,
    addNote: PropTypes.func,
    currency: PropTypes.string,
    onDrop: PropTypes.func,
    portfolioNode: PropTypes.object,
    relay: PropTypes.object,
    reordered: PropTypes.bool,
    searchTerm: PropTypes.string,
    setNumItems: PropTypes.func,
    setPartialPageLoading: PropTypes.func,
    setTotalResults: PropTypes.func,
    showDealerBranding: PropTypes.bool,
    showSeller: PropTypes.bool,
    showProjects: PropTypes.bool,
    sortBy: PropTypes.string,
    type: PropTypes.string,
    user: PropTypes.object,
    viewMode: PropTypes.string,
    viewer: PropTypes.object,
    page: PropTypes.number,
    changePage: PropTypes.func,
    resetUiState: PropTypes.func,
    setFullPageLoading: PropTypes.func,
    setSearchTerm: PropTypes.func,
    setSortBy: PropTypes.func,
};

const mapStateToProps = state => {
    return {
        isLoadingPartialPage: state.favorites.uiState.isLoadingPartialPage,
        totalResults: state.favorites.uiState.totalResults,
        page: state.favorites.uiState.page,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setNumItems: first => {
            dispatch(actions.setNumItems({ first }));
        },
        setPartialPageLoading: isLoadingPartialPage => {
            dispatch(actions.setPartialPageLoading({ isLoadingPartialPage }));
        },
        setTotalResults: totalResults => {
            dispatch(actions.setTotalResults({ totalResults }));
        },
        setPage: page => {
            dispatch(actions.setPage({ page }));
        },
        resetUiState: ({ retainItems = false, retainSort = false, retainSearch = false } = {}) => {
            dispatch(actions.resetUiState({ retainItems, retainSort, retainSearch }));
        },
        setFullPageLoading: isLoadingFullPage => {
            dispatch(actions.setFullPageLoading({ isLoadingFullPage }));
        },
        setSearchTerm: searchTerm => {
            dispatch(actions.setSearchTerm({ searchTerm }));
        },
        setSortBy: sortBySelection => {
            dispatch(actions.setSortBy({ sortBySelection }));
        },
    };
};

const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(ItemsRefetchWrapper);

ItemsRefetchWrapper = createRefetchContainer(
    ReduxWrapped,
    {
        portfolioNode: graphql`
            fragment ItemsRefetchWrapper_portfolioNode on PortfolioV2 {
                id
                serviceId
                items(
                    first: $first
                    after: $after
                    searchTerm: $searchTerm
                    sortBy: $sortBy
                    page: $page
                ) {
                    totalResults
                    numberOfPages
                    pageInfo {
                        hasNextPage
                        endCursor
                    }
                    edges {
                        cursor
                        node {
                            item {
                                serviceId
                                vertical
                                seller {
                                    serviceId
                                }
                            }
                        }
                    }
                    ...Tiles_portfolioItemsConnection
                }
            }
        `,
    },
    graphql`
        query ItemsRefetchWrapperRefetchQuery(
            $after: String
            $first: Int!
            $searchTerm: String
            $sortBy: PortfolioItemsSortByTypes
            $showSeller: Boolean!
            $userId: String!
            $hasUserId: Boolean!
            $isTrade: Boolean!
            $page: Int!
            $portfolioIds: [String]
        ) {
            viewer {
                portfolios(first: 1, portfolioIds: $portfolioIds) {
                    edges {
                        node {
                            ...ItemsRefetchWrapper_portfolioNode
                        }
                    }
                }
            }
        }
    `
);

export default ItemsRefetchWrapper;
